import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Loader2, RefreshCcw, Search, Plus, History } from 'lucide-react';
import { toast } from 'sonner';
import { useStaffAuth } from '../../context/StaffAuthContext';
import { apiErrorMessage } from '../../lib/apiError';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import LeadHistoryDrawer from '../../components/LeadHistoryDrawer';

const STATUSES = ['new', 'contacted', 'qualified', 'won', 'lost'];
const SOURCES = ['website', 'partner', 'manual'];
const CURRENCIES = ['GBP', 'EUR', 'USD', 'AUD', 'CAD', 'JPY', 'AED', 'ZAR'];

const STATUS_STYLES = {
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-amber-100 text-amber-800',
  qualified: 'bg-emerald-100 text-emerald-800',
  won: 'bg-[#0B3B2E] text-[#F5F1E8]',
  lost: 'bg-rose-100 text-rose-800',
};

const STATUS_LABEL = { new: 'New', contacted: 'Contacted', qualified: 'Qualified', won: 'Won', lost: 'Lost' };

function fmtValue(v, ccy) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return `${ccy || 'GBP'} ${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
}

export default function CrmLeads() {
  const { staff, authHeader, API } = useStaffAuth();
  const [leads, setLeads] = useState([]);
  const [rms, setRms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [historyLead, setHistoryLead] = useState(null);

  const canReassign = staff.role !== 'rm';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;
      const [leadsRes, staffRes] = await Promise.all([
        axios.get(`${API}/crm/leads`, { headers: authHeader(), params }),
        axios.get(`${API}/crm/staff`, { headers: authHeader() }),
      ]);
      setLeads(leadsRes.data.leads || []);
      setRms((staffRes.data.staff || []).filter((s) => s.active));
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not load leads'));
    } finally {
      setLoading(false);
    }
  }, [API, authHeader, statusFilter, sourceFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = leads.filter((l) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      l.name?.toLowerCase().includes(s) ||
      l.email?.toLowerCase().includes(s) ||
      (l.phone || '').toLowerCase().includes(s)
    );
  });

  // Pipeline summary: counts + total value per status (in a common currency = GBP for MVP display)
  const pipeline = useMemo(() => {
    const summary = STATUSES.reduce((acc, s) => {
      acc[s] = { count: 0, byCcy: {} };
      return acc;
    }, {});
    for (const l of leads) {
      const s = summary[l.status];
      if (!s) continue;
      s.count += 1;
      if (l.deal_value !== null && l.deal_value !== undefined && l.deal_value !== '') {
        const ccy = l.deal_currency || 'GBP';
        s.byCcy[ccy] = (s.byCcy[ccy] || 0) + Number(l.deal_value);
      }
    }
    return summary;
  }, [leads]);

  const updateLead = async (id, patch) => {
    try {
      const res = await axios.patch(`${API}/crm/leads/${id}`, patch, { headers: authHeader() });
      setLeads((prev) => prev.map((l) => (l.id === id ? res.data.lead : l)));
      toast.success('Lead updated');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Update failed'));
    }
  };

  const rmName = (id) => (id ? rms.find((r) => r.id === id)?.name || '—' : '—');

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#0B3B2E]/70 font-medium">CRM</p>
          <h1 className="mt-2 font-serif text-[32px] lg:text-[40px] leading-tight text-[#0B3B2E]">Leads</h1>
          <p className="mt-1 text-[13px] text-[#0E1F1A]/60">
            {staff.role === 'rm' ? 'Leads assigned to you.' : 'All leads across the team.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={load} variant="outline"
            className="h-10 rounded-full border-[#0B3B2E]/25 bg-transparent text-[#0B3B2E] hover:bg-[#0B3B2E]/5">
            <RefreshCcw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          <Button onClick={() => setNewOpen(true)}
            className="h-10 rounded-full bg-[#0B3B2E] text-[#F5F1E8] hover:bg-[#12503F]">
            <Plus className="w-4 h-4 mr-1.5" /> New lead
          </Button>
        </div>
      </div>

      {/* Pipeline summary */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
        {STATUSES.map((s) => {
          const cell = pipeline[s];
          const ccyEntries = Object.entries(cell.byCcy);
          return (
            <div key={s} className="bg-white rounded-2xl border border-[#0B3B2E]/10 p-4">
              <div className="flex items-center justify-between">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${STATUS_STYLES[s]}`}>
                  {STATUS_LABEL[s]}
                </span>
                <span className="text-[11px] text-[#0E1F1A]/55">{cell.count}</span>
              </div>
              <div className="mt-3 min-h-[38px]">
                {ccyEntries.length === 0 ? (
                  <p className="font-serif text-[20px] text-[#0B3B2E]/40">—</p>
                ) : (
                  ccyEntries.slice(0, 2).map(([ccy, total]) => (
                    <p key={ccy} className="font-serif text-[18px] text-[#0B3B2E] leading-tight">
                      {ccy} {total.toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                    </p>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0B3B2E]/50" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / email / phone"
            className="pl-9 h-10 bg-white border-[#0B3B2E]/15" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 rounded-md bg-white border border-[#0B3B2E]/15 px-3 text-[14px]">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}
          className="h-10 rounded-md bg-white border border-[#0B3B2E]/15 px-3 text-[14px]">
          <option value="">All sources</option>
          {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="mt-6 bg-white rounded-2xl border border-[#0B3B2E]/10 overflow-hidden">
        {loading ? (
          <div className="p-16 flex items-center justify-center text-[#0B3B2E]">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center text-[#0E1F1A]/55 text-[14px]">No leads yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-[#0B3B2E]/5 text-[#0B3B2E]">
                <tr>
                  <Th>Name</Th><Th>Contact</Th><Th>Interest</Th>
                  <Th>Value</Th><Th>Source</Th><Th>Status</Th>
                  <Th>Assigned RM</Th><Th>Created</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} className="border-t border-[#0B3B2E]/8 hover:bg-[#F5F1E8]/40">
                    <Td>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setHistoryLead(l)}
                          className="w-7 h-7 rounded-full text-[#0B3B2E]/70 hover:text-[#0B3B2E] hover:bg-[#0B3B2E]/8 flex items-center justify-center transition-colors"
                          title="View activity"
                        >
                          <History className="w-4 h-4" />
                        </button>
                        <div>
                          <p className="font-medium text-[#0B3B2E]">{l.name}</p>
                          {l.message && <p className="text-[11px] text-[#0E1F1A]/50 line-clamp-1 max-w-[220px]">{l.message}</p>}
                        </div>
                      </div>
                    </Td>
                    <Td>
                      <p>{l.email}</p>
                      {l.phone && <p className="text-[11px] text-[#0E1F1A]/55">{l.phone}</p>}
                    </Td>
                    <Td className="capitalize">{l.interest}</Td>
                    <Td>
                      <DealValueCell lead={l} onSave={(patch) => updateLead(l.id, patch)} />
                    </Td>
                    <Td>
                      <span className="text-[11px] uppercase tracking-wider text-[#0E1F1A]/60">{l.source}</span>
                    </Td>
                    <Td>
                      <select value={l.status}
                        onChange={(e) => updateLead(l.id, { status: e.target.value })}
                        className={`h-8 rounded-full px-2.5 text-[11px] font-medium border-0 outline-none ${STATUS_STYLES[l.status] || ''}`}>
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </Td>
                    <Td>
                      {canReassign ? (
                        <div className="space-y-1">
                          <select value={l.assigned_rm_id || ''}
                            onChange={(e) => updateLead(l.id, { assigned_rm_id: e.target.value || null })}
                            className="h-8 rounded-md border border-[#0B3B2E]/15 bg-white px-2 text-[12px] max-w-[180px]">
                            <option value="">Unassigned</option>
                            {rms.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name} ({r.role})
                              </option>
                            ))}
                          </select>
                          {!l.assigned_rm_id && l.suggested_rm_id && (
                            <button type="button"
                              onClick={() => updateLead(l.id, { assigned_rm_id: l.suggested_rm_id })}
                              className="text-[11px] text-[#0B3B2E] underline underline-offset-2 decoration-dotted hover:text-[#12503F]">
                              Suggested: {rmName(l.suggested_rm_id)} — accept
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-[#0E1F1A]/70">{rmName(l.assigned_rm_id)}</span>
                      )}
                    </Td>
                    <Td className="whitespace-nowrap text-[#0E1F1A]/55">
                      {l.created_at ? new Date(l.created_at).toLocaleDateString('en-GB') : '—'}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <NewLeadModal open={newOpen} onOpenChange={setNewOpen} onCreated={load} rms={rms} />
      <LeadHistoryDrawer
        open={!!historyLead}
        onOpenChange={(v) => !v && setHistoryLead(null)}
        lead={historyLead}
      />
    </div>
  );
}

function DealValueCell({ lead, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(lead.deal_value ?? '');
  const [ccy, setCcy] = useState(lead.deal_currency || 'GBP');

  useEffect(() => {
    setVal(lead.deal_value ?? '');
    setCcy(lead.deal_currency || 'GBP');
  }, [lead]);

  const save = async () => {
    const patch = {};
    const numeric = val === '' ? null : Number(val);
    if (numeric !== null && (Number.isNaN(numeric) || numeric < 0)) return;
    if (numeric !== (lead.deal_value ?? null)) patch.deal_value = numeric;
    if (ccy !== (lead.deal_currency || 'GBP')) patch.deal_currency = ccy;
    setEditing(false);
    if (Object.keys(patch).length > 0) await onSave(patch);
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="text-left hover:text-[#0B3B2E] hover:underline underline-offset-2 decoration-dotted"
        title="Click to edit"
      >
        {fmtValue(lead.deal_value, lead.deal_currency)}
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <select value={ccy} onChange={(e) => setCcy(e.target.value)}
        className="h-8 rounded-md border border-[#0B3B2E]/20 bg-white px-1.5 text-[12px]">
        {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <input
        type="number"
        min="0"
        step="1"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
        onBlur={save}
        autoFocus
        className="h-8 w-24 rounded-md border border-[#0B3B2E]/20 bg-white px-2 text-[12px]"
      />
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-medium">{children}</th>;
}
function Td({ children, className = '' }) {
  return <td className={`px-4 py-3 align-top text-[13px] text-[#0E1F1A]/85 ${className}`}>{children}</td>;
}

function NewLeadModal({ open, onOpenChange, onCreated, rms }) {
  const { authHeader, API } = useStaffAuth();
  const [form, setForm] = useState({
    name: '', email: '', phone: '', interest: 'other', message: '', assigned_rm_id: '',
    deal_value: '', deal_currency: 'GBP',
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error('Name and email are required');
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.assigned_rm_id) delete payload.assigned_rm_id;
      if (!payload.phone) delete payload.phone;
      if (!payload.message) delete payload.message;
      if (payload.deal_value === '' || payload.deal_value === null) {
        delete payload.deal_value;
      } else {
        payload.deal_value = Number(payload.deal_value);
      }
      await axios.post(`${API}/crm/leads`, payload, { headers: authHeader() });
      toast.success('Lead created');
      setForm({
        name: '', email: '', phone: '', interest: 'other', message: '', assigned_rm_id: '',
        deal_value: '', deal_currency: 'GBP',
      });
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Create failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[520px] bg-[#F5F1E8]">
        <DialogTitle asChild>
          <h2 className="font-serif text-[24px] text-[#0B3B2E]">New lead</h2>
        </DialogTitle>
        <DialogDescription className="text-[13px] text-[#0E1F1A]/60">
          Add a lead manually to the pipeline.
        </DialogDescription>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Row label="Name"><Input value={form.name} onChange={(e) => set('name', e.target.value)} /></Row>
            <Row label="Email"><Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></Row>
            <Row label="Phone"><Input value={form.phone} onChange={(e) => set('phone', e.target.value)} /></Row>
            <Row label="Interest">
              <select value={form.interest} onChange={(e) => set('interest', e.target.value)}
                className="h-10 w-full rounded-md border border-[#0B3B2E]/20 bg-white px-3 text-[14px]">
                <option value="property">Property</option>
                <option value="business">Business</option>
                <option value="personal">Personal</option>
                <option value="other">Other</option>
              </select>
            </Row>
          </div>
          <Row label="Expected deal value">
            <div className="flex gap-2">
              <select value={form.deal_currency} onChange={(e) => set('deal_currency', e.target.value)}
                className="h-10 rounded-md border border-[#0B3B2E]/20 bg-white px-3 text-[14px]">
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <Input type="number" min="0" value={form.deal_value}
                onChange={(e) => set('deal_value', e.target.value)}
                placeholder="e.g. 250000" className="flex-1" />
            </div>
          </Row>
          <Row label="Assign to">
            <select value={form.assigned_rm_id} onChange={(e) => set('assigned_rm_id', e.target.value)}
              className="h-10 w-full rounded-md border border-[#0B3B2E]/20 bg-white px-3 text-[14px]">
              <option value="">Unassigned</option>
              {rms.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.role})</option>)}
            </select>
          </Row>
          <Row label="Notes">
            <textarea value={form.message} onChange={(e) => set('message', e.target.value)} rows={3}
              className="w-full rounded-md border border-[#0B3B2E]/20 bg-white p-2.5 text-[14px]" />
          </Row>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
              className="rounded-full border-[#0B3B2E]/25 bg-transparent text-[#0B3B2E]">Cancel</Button>
            <Button type="submit" disabled={busy}
              className="rounded-full bg-[#0B3B2E] text-[#F5F1E8] hover:bg-[#12503F]">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[12px] text-[#0E1F1A]/65">{label}</Label>
      {children}
    </div>
  );
}
